import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

// Promise-based replacements for window.confirm / window.prompt.
//
//   const confirm = useConfirm();
//   if (!(await confirm({ title: 'Delete task?', confirmText: 'Delete', destructive: true }))) return;
//
//   const prompt = usePrompt();
//   const name = await prompt({ title: 'Key name', label: 'Name' });   // null when cancelled
//
// One <DialogHost /> is mounted at the App root. Requests are queued, so two
// overlapping calls show one dialog at a time instead of clobbering each other.
// The hooks return module-level functions (stable identity), so they are safe
// to list in effect/callback deps, and the same functions can be imported
// directly (confirmDialog / promptDialog) from non-component code.

let enqueue = null; // set by the mounted host
let idSeq = 0;

function warnNoHost(kind) {
  // Resolve as "cancelled" rather than hang the caller forever.
  // eslint-disable-next-line no-console
  console.error(`${kind}Dialog() called but no <DialogHost /> is mounted; treating as cancelled.`);
}

function normalize(opts, defaults) {
  const o = typeof opts === 'string' ? { title: opts } : { ...(opts || {}) };
  return { ...defaults, ...o };
}

export function confirmDialog(opts) {
  const req = {
    id: ++idSeq,
    kind: 'confirm',
    ...normalize(opts, { title: 'Are you sure?', confirmText: 'Confirm', cancelText: 'Cancel', destructive: false }),
  };
  if (!enqueue) { warnNoHost('confirm'); return Promise.resolve(false); }
  return new Promise((resolve) => enqueue({ ...req, resolve }));
}

export function promptDialog(opts) {
  const req = {
    id: ++idSeq,
    kind: 'prompt',
    ...normalize(opts, {
      title: 'Enter a value',
      confirmText: 'OK',
      cancelText: 'Cancel',
      defaultValue: '',
      placeholder: '',
      multiline: false,
      allowEmpty: false,
      validate: null, // (value) => error message | null
    }),
  };
  if (!enqueue) { warnNoHost('prompt'); return Promise.resolve(null); }
  return new Promise((resolve) => enqueue({ ...req, resolve }));
}

export function useConfirm() { return confirmDialog; }
export function usePrompt() { return promptDialog; }

function ConfirmView({ req, onDone }) {
  return (
    <AlertDialog open onOpenChange={(open) => { if (!open) onDone(false); }}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif text-brand-navy">{req.title}</AlertDialogTitle>
          {req.description ? (
            <AlertDialogDescription>{req.description}</AlertDialogDescription>
          ) : (
            <AlertDialogDescription className="sr-only">{req.title}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onDone(false)}
            data-testid="confirm-dialog-cancel"
          >
            {req.cancelText}
          </Button>
          <Button
            type="button"
            variant={req.destructive ? 'destructive' : 'default'}
            onClick={() => onDone(true)}
            data-testid="confirm-dialog-ok"
          >
            {req.confirmText}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PromptView({ req, onDone }) {
  const [value, setValue] = useState(req.defaultValue || '');
  const [error, setError] = useState(null);
  const trimmed = value.trim();
  const canSubmit = req.allowEmpty || trimmed.length > 0;
  const submit = (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    const msg = req.validate ? req.validate(trimmed) : null;
    if (msg) { setError(msg); return; }
    onDone(trimmed);
  };
  const Field = req.multiline ? Textarea : Input;
  const inputId = `prompt-dialog-input-${req.id}`;
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onDone(null); }}>
      <DialogContent className="max-w-md" data-testid="prompt-dialog">
        <form onSubmit={submit} className="contents">
          <DialogHeader>
            <DialogTitle className="font-serif text-brand-navy">{req.title}</DialogTitle>
            {req.description ? (
              <DialogDescription>{req.description}</DialogDescription>
            ) : (
              <DialogDescription className="sr-only">{req.title}</DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-2">
            {req.label ? <Label htmlFor={inputId}>{req.label}</Label> : null}
            <Field
              id={inputId}
              autoFocus
              value={value}
              placeholder={req.placeholder}
              onChange={(e) => { setValue(e.target.value); if (error) setError(null); }}
              aria-invalid={error ? 'true' : undefined}
              onKeyDown={(e) => {
                // Enter submits a single-line prompt; Cmd/Ctrl+Enter submits a textarea.
                if (e.key === 'Enter' && (!req.multiline || e.metaKey || e.ctrlKey)) submit(e);
              }}
              data-testid="prompt-dialog-input"
            />
            {error ? <p className="text-xs text-destructive" data-testid="prompt-dialog-error">{error}</p> : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onDone(null)}
              data-testid="prompt-dialog-cancel"
            >
              {req.cancelText}
            </Button>
            <Button type="submit" disabled={!canSubmit} data-testid="prompt-dialog-ok">
              {req.confirmText}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function DialogHost() {
  const [queue, setQueue] = useState([]);
  const queueRef = useRef(queue);
  queueRef.current = queue;

  useEffect(() => {
    enqueue = (req) => setQueue((q) => [...q, req]);
    return () => { enqueue = null; };
  }, []);

  const current = queue[0];
  const done = useCallback((result) => {
    // Resolve outside the state updater so StrictMode's double-invoke can't
    // touch the promise twice.
    queueRef.current[0]?.resolve(result);
    setQueue((q) => q.slice(1));
  }, []);

  if (!current) return null;
  // Key by request id so a queued prompt gets fresh local state.
  return current.kind === 'prompt'
    ? <PromptView key={current.id} req={current} onDone={done} />
    : <ConfirmView key={current.id} req={current} onDone={done} />;
}
